import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { 
  History, 
  ChevronDown, 
  ChevronUp,
  TrendingUp,
  TrendingDown,
  Plus,
  Minus,
  Edit3,
  RotateCcw,
  FileText,
  Calendar,
  User,
  MessageSquare,
  Utensils,
  RefreshCw
} from "lucide-react";
import { cn } from "../../lib/utils";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Format relative time
const formatRelativeTime = (timestamp) => {
  const now = new Date();
  const date = new Date(timestamp);
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  const diffWeeks = Math.floor(diffDays / 7);
  
  if (diffMins < 1) return "Ahora mismo";
  if (diffMins < 60) return `Hace ${diffMins} min`;
  if (diffHours < 24) return `Hace ${diffHours}h`;
  if (diffDays === 1) return "Ayer";
  if (diffDays < 7) return `Hace ${diffDays} días`;
  if (diffWeeks === 1) return "Hace 1 semana";
  if (diffWeeks < 4) return `Hace ${diffWeeks} semanas`;
  
  return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
};

// Format date for display
const formatDate = (timestamp) => {
  return new Date(timestamp).toLocaleDateString('es-ES', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Get icon for change type
const getChangeTypeIcon = (type) => {
  switch (type) {
    case 'adjustment': return Edit3;
    case 'assignment': return Utensils;
    case 'reset': return RotateCcw;
    case 'note': return MessageSquare;
    default: return FileText;
  }
};

// Get label for change type
const getChangeTypeLabel = (type) => {
  switch (type) {
    case 'adjustment': return 'Ajuste de dieta';
    case 'assignment': return 'Dieta asignada';
    case 'reset': return 'Reset a plantilla';
    case 'note': return 'Nota del coach';
    default: return 'Cambio';
  }
};

// Macro Change Badge
const MacroChangeBadge = ({ label, diff, unit = 'g' }) => {
  const isPositive = diff > 0;
  
  return (
    <div className={cn(
      "flex items-center gap-1 px-2 py-1 rounded-full text-xs",
      isPositive ? "bg-green-500/10 text-green-400" : "bg-orange-500/10 text-orange-400"
    )}>
      {isPositive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
      <span>{label}</span>
      <span className="font-bold">{isPositive ? '+' : ''}{diff}{unit}</span>
    </div>
  );
};

// History Entry Component
const HistoryEntry = ({ entry, isFirst }) => {
  const [expanded, setExpanded] = useState(false);
  const Icon = getChangeTypeIcon(entry.type);
  
  const hasMacroChanges = entry.macro_changes && Object.keys(entry.macro_changes).length > 0;
  const hasMealChanges = entry.meal_changes && Object.keys(entry.meal_changes).length > 0;
  const hasDetails = hasMacroChanges || hasMealChanges || entry.note;
  
  // Generate summary text
  const getSummary = () => {
    const parts = [];
    
    if (entry.macro_changes?.calories) {
      const diff = entry.macro_changes.calories.diff;
      parts.push(`${diff > 0 ? '+' : ''}${diff} kcal`);
    }
    if (entry.macro_changes?.protein) {
      const diff = entry.macro_changes.protein.diff;
      parts.push(`${diff > 0 ? '+' : ''}${diff}g proteína`);
    }
    if (entry.macro_changes?.carbs) {
      const diff = entry.macro_changes.carbs.diff;
      parts.push(`${diff > 0 ? '+' : ''}${diff}g carbos`);
    }
    if (entry.macro_changes?.fat) {
      const diff = entry.macro_changes.fat.diff;
      parts.push(`${diff > 0 ? '+' : ''}${diff}g grasas`);
    }
    
    if (entry.meal_changes?.foods_added?.length) {
      parts.push(`+${entry.meal_changes.foods_added.length} alimentos`);
    }
    if (entry.meal_changes?.foods_removed?.length) {
      parts.push(`-${entry.meal_changes.foods_removed.length} alimentos`);
    }
    
    if (parts.length === 0 && entry.note) {
      return entry.note.slice(0, 50) + (entry.note.length > 50 ? '...' : '');
    }
    
    return parts.slice(0, 3).join(' • ') || getChangeTypeLabel(entry.type);
  };

  return (
    <div className="relative">
      {/* Timeline connector */}
      {!isFirst && (
        <div className="absolute left-[15px] -top-4 w-0.5 h-4 bg-[#2A2A2A]" />
      )}
      
      <div 
        className={cn(
          "bg-[#1A1A1A] rounded-lg border transition-all",
          expanded ? "border-[#D4AF37]/30" : "border-[#2A2A2A]",
          hasDetails && "cursor-pointer hover:border-[#3A3A3A]"
        )}
        onClick={() => hasDetails && setExpanded(!expanded)}
      >
        {/* Header */}
        <div className="flex items-start gap-3 p-3">
          {/* Icon */}
          <div className={cn(
            "w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0",
            entry.type === 'adjustment' ? "bg-[#D4AF37]/10" : "bg-[#2A2A2A]"
          )}>
            <Icon className={cn(
              "w-4 h-4",
              entry.type === 'adjustment' ? "text-[#D4AF37]" : "text-[#666]"
            )} />
          </div>
          
          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-white text-sm font-medium truncate">
                {getSummary()}
              </span>
            </div>
            
            <div className="flex items-center gap-2 text-[10px] text-[#666]">
              <span>{formatRelativeTime(entry.timestamp)}</span>
              <span>•</span>
              <span>{entry.coach_name || 'Coach'}</span>
            </div>
          </div>
          
          {/* Expand button */}
          {hasDetails && (
            <button className="p-1 text-[#666] hover:text-white">
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          )}
        </div>
        
        {/* Expanded details */}
        {expanded && (
          <div className="px-3 pb-3 space-y-3 border-t border-[#2A2A2A] pt-3">
            {/* Macro changes */}
            {hasMacroChanges && (
              <div>
                <div className="text-[#888] text-[10px] mb-2">CAMBIOS EN MACROS</div>
                <div className="flex flex-wrap gap-2">
                  {entry.macro_changes.calories && (
                    <MacroChangeBadge 
                      label="Calorías" 
                      diff={entry.macro_changes.calories.diff} 
                      unit=" kcal" 
                    />
                  )}
                  {entry.macro_changes.protein && (
                    <MacroChangeBadge 
                      label="Proteína" 
                      diff={entry.macro_changes.protein.diff} 
                    />
                  )}
                  {entry.macro_changes.carbs && (
                    <MacroChangeBadge 
                      label="Carbos" 
                      diff={entry.macro_changes.carbs.diff} 
                    />
                  )}
                  {entry.macro_changes.fat && (
                    <MacroChangeBadge 
                      label="Grasas" 
                      diff={entry.macro_changes.fat.diff} 
                    />
                  )}
                </div>
              </div>
            )}
            
            {/* Meal changes */}
            {hasMealChanges && (
              <div>
                <div className="text-[#888] text-[10px] mb-2">CAMBIOS EN COMIDAS</div>
                <div className="space-y-1">
                  {entry.meal_changes.meals_added?.map((meal, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-green-400">
                      <Plus className="w-3 h-3" />
                      <span>Comida añadida: {meal}</span>
                    </div>
                  ))}
                  {entry.meal_changes.meals_removed?.map((meal, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-red-400">
                      <Minus className="w-3 h-3" />
                      <span>Comida eliminada: {meal}</span>
                    </div>
                  ))}
                  {entry.meal_changes.foods_added?.slice(0, 3).map((food, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-green-400">
                      <Plus className="w-3 h-3" />
                      <span>+{food.amount}g en {food.meal}</span>
                    </div>
                  ))}
                  {entry.meal_changes.foods_removed?.slice(0, 3).map((food, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-red-400">
                      <Minus className="w-3 h-3" />
                      <span>-{food.amount}g en {food.meal}</span>
                    </div>
                  ))}
                  {entry.meal_changes.foods_modified?.slice(0, 3).map((food, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-yellow-400">
                      <Edit3 className="w-3 h-3" />
                      <span>{food.old_amount}g → {food.new_amount}g en {food.meal}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Snapshots comparison */}
            {entry.snapshot_before && entry.snapshot_after && (
              <div>
                <div className="text-[#888] text-[10px] mb-2">COMPARACIÓN</div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="p-2 bg-[#0B0B0B] rounded border border-[#2A2A2A]">
                    <div className="text-[#666] text-[10px] mb-1">ANTES</div>
                    <div className="text-[#888]">{entry.snapshot_before.calories_target || 0} kcal</div>
                    <div className="text-red-400/70">{entry.snapshot_before.protein_target || 0}g P</div>
                  </div>
                  <div className="p-2 bg-[#D4AF37]/5 rounded border border-[#D4AF37]/20">
                    <div className="text-[#D4AF37] text-[10px] mb-1">DESPUÉS</div>
                    <div className="text-white">{entry.snapshot_after.calories_target || 0} kcal</div>
                    <div className="text-red-400">{entry.snapshot_after.protein_target || 0}g P</div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Note */}
            {entry.note && (
              <div>
                <div className="text-[#888] text-[10px] mb-1">NOTA DEL COACH</div>
                <p className="text-[#CCC] text-xs bg-[#0B0B0B] p-2 rounded border border-[#2A2A2A]">
                  {entry.note}
                </p>
              </div>
            )}
            
            {/* Timestamp */}
            <div className="text-[#555] text-[10px]">
              {formatDate(entry.timestamp)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Main Diet History Component
const DietHistory = ({ memberId, onClose }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddNote, setShowAddNote] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const { getAuthHeaders } = useAuth();

  const loadHistory = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/admin/members/${memberId}/diet-history`,
        { headers: getAuthHeaders() }
      );
      setHistory(response.data.history || []);
    } catch (error) {
      console.error("Error loading history:", error);
    } finally {
      setLoading(false);
    }
  }, [memberId, getAuthHeaders]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    
    setSavingNote(true);
    try {
      await axios.post(
        `${API_URL}/api/admin/members/${memberId}/diet-history`,
        { type: 'note', note: newNote.trim() },
        { headers: getAuthHeaders() }
      );
      toast.success("Nota añadida al historial");
      setNewNote("");
      setShowAddNote(false);
      loadHistory();
    } catch (error) {
      toast.error("Error al añadir nota");
    } finally {
      setSavingNote(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <History className="w-5 h-5 text-[#D4AF37]" />
          <h3 className="text-white font-semibold">Historial de Cambios</h3>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowAddNote(true)}
          className="border-[#2A2A2A] text-[#888] hover:text-white"
        >
          <MessageSquare className="w-4 h-4 mr-2" />
          Añadir Nota
        </Button>
      </div>

      {/* Add note form */}
      {showAddNote && (
        <div className="p-3 bg-[#1A1A1A] rounded-lg border border-[#2A2A2A] space-y-3">
          <Textarea
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            placeholder="Ej: Subimos carbohidratos por fatiga en entrenamientos..."
            className="bg-[#0B0B0B] border-[#2A2A2A] text-white min-h-[80px]"
          />
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setShowAddNote(false); setNewNote(""); }}
              className="border-[#2A2A2A] text-[#888]"
            >
              Cancelar
            </Button>
            <Button
              size="sm"
              onClick={handleAddNote}
              disabled={!newNote.trim() || savingNote}
              className="bg-[#D4AF37] hover:bg-[#B8962E] text-[#0B0B0B]"
            >
              {savingNote ? "Guardando..." : "Guardar Nota"}
            </Button>
          </div>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="w-5 h-5 text-[#D4AF37] animate-spin" />
        </div>
      ) : history.length === 0 ? (
        <div className="text-center py-8">
          <History className="w-10 h-10 text-[#333] mx-auto mb-3" />
          <p className="text-[#666] text-sm">No hay historial de cambios</p>
          <p className="text-[#555] text-xs mt-1">Los cambios en la dieta se registrarán aquí</p>
        </div>
      ) : (
        <div className="space-y-4">
          {history.map((entry, idx) => (
            <HistoryEntry 
              key={entry.id} 
              entry={entry} 
              isFirst={idx === 0}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default DietHistory;
